import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  DesignTestInputDto,
  DesignTestOutputDto
} from '../../../models/job-request-iterations-dto.interface';

interface PsdSimpleRow {
  label: string;
  value: number;
}

interface PsdReportRow {
  percentile?: number;
  sizeLessThan?: number;
  diameter?: number;
  cumulativeLess?: number;
}

@Component({
  selector: 'app-psd-test-report',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './psd-test-report.component.html',
  styleUrl: './psd-test-report.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PsdTestReportComponent {
  private static readonly DISPLAY_PERCENTILES = [10, 50, 90];
  private static readonly ALL_PERCENTILES = [5, 10, 25, 40, 50, 60, 75, 85, 90, 95];
  private static readonly DISPLAY_DIAMETERS = [6, 44, 74];

  @Input({ required: true }) test!: DesignTestInputDto;
  @Input({ required: true }) output!: DesignTestOutputDto;

  protected psdSimpleReportValues(): PsdSimpleRow[] | null {
    const percentile = this.firstDirectNumber('percentile');
    const size = this.firstDirectNumber('size');
    const percentChannel = this.firstDirectNumber('percentchannel');

    if (percentile === undefined && size === undefined && percentChannel === undefined) {
      return null;
    }

    return [
      { label: 'Percentile', value: percentile },
      { label: 'Size', value: size },
      { label: 'Percent Channel', value: percentChannel }
    ].filter((row): row is PsdSimpleRow => row.value !== undefined);
  }

  protected psdReportRows(): PsdReportRow[] | null {
    const percentileValues = PsdTestReportComponent.ALL_PERCENTILES.map(percentile => ({
      percentile,
      sizeLessThan: this.firstNumber([
        `percentile${percentile}_size`,
        `d${percentile}`
      ]),
      percentChannel: this.firstNumber([`percentile${percentile}`])
    }));
    const preferredPercentiles = percentileValues.filter(row =>
      PsdTestReportComponent.DISPLAY_PERCENTILES.includes(row.percentile));
    const hasPreferredValues = preferredPercentiles.some(row =>
      row.sizeLessThan !== undefined || row.percentChannel !== undefined);
    const visiblePercentiles = hasPreferredValues
      ? preferredPercentiles
      : percentileValues.filter(row => row.sizeLessThan !== undefined || row.percentChannel !== undefined);
    const diameterValues = PsdTestReportComponent.DISPLAY_DIAMETERS.map(diameter => ({
      diameter,
      cumulativeLess: this.firstNumber([
        `size${diameter}_percentile`,
        `diameter${diameter}_percentile`
      ])
    }));

    if (visiblePercentiles.length === 0 && diameterValues.every(row => row.cumulativeLess === undefined)) {
      return null;
    }

    const rowCount = Math.max(visiblePercentiles.length, diameterValues.length);
    return Array.from({ length: rowCount }, (_, index) => ({
      percentile: visiblePercentiles[index]?.percentile,
      sizeLessThan: visiblePercentiles[index]?.sizeLessThan,
      diameter: diameterValues[index]?.diameter,
      cumulativeLess: diameterValues[index]?.cumulativeLess
    }));
  }

  protected formatValue(value: number | undefined): string {
    if (value === undefined) return '—';
    return value.toFixed(2).replace(/\.?(0+)$/, '');
  }

  private firstDirectNumber(name: string): number | undefined {
    for (const source of [this.output, this.test]) {
      const value = this.directValue(name, source);
      if (value !== null) {
        const parsed = this.toNumber(value);
        if (parsed !== undefined) return parsed;
      }
    }

    return undefined;
  }

  private firstNumber(names: string[]): number | undefined {
    for (const source of [this.output, this.test]) {
      const value = this.value(names, source);
      if (value !== null) {
        const parsed = this.toNumber(value);
        if (parsed !== undefined) return parsed;
      }
    }

    return undefined;
  }

  private directValue(
    name: string,
    source: DesignTestInputDto | DesignTestOutputDto
  ): string | null {
    const acceptedName = this.normalize(name);
    const fields = 'parsedValues' in source ? source.parsedValues : source.parsedInputValues;
    const parsedField = fields.find(field => this.normalize(field.label) === acceptedName);
    if (parsedField?.value?.trim()) return parsedField.value.trim();

    const values = this.simpleXmlValues(source.content);
    return values.get(acceptedName) ?? null;
  }

  private value(
    names: string[],
    source: DesignTestInputDto | DesignTestOutputDto
  ): string | null {
    const acceptedNames = new Set(names.map(name => this.normalize(name)));
    const fields = 'parsedValues' in source ? source.parsedValues : source.parsedInputValues;
    const parsedField = fields.find(field => acceptedNames.has(this.normalize(field.label)));
    if (parsedField?.value?.trim()) return parsedField.value.trim();

    const values = this.psdXmlValues(source.content);
    for (const name of acceptedNames) {
      const value = values.get(name);
      if (value) return value;
    }

    return null;
  }

  private simpleXmlValues(content: string | null): Map<string, string> {
    const values = new Map<string, string>();
    if (!content) return values;

    const document = new DOMParser().parseFromString(content, 'application/xml');
    if (document.querySelector('parsererror')) return values;

    const records = Array.from(document.getElementsByTagName('*'))
      .filter(element => this.childValue(element, 'percentile') !== null
        && this.childValue(element, 'size') !== null
        && this.childValue(element, 'percentchannel') !== null);
    const record = records[0];
    if (!record) return values;

    for (const name of ['percentile', 'size', 'percentchannel']) {
      const value = this.childValue(record, name);
      if (value !== null) values.set(name, value);
    }

    return values;
  }

  private psdXmlValues(content: string | null): Map<string, string> {
    const values = new Map<string, string>();
    if (!content) return values;

    const document = new DOMParser().parseFromString(content, 'application/xml');
    if (document.querySelector('parsererror')) return values;

    for (const section of this.elementsNamed(document, 'percentiles')) {
      for (const record of Array.from(section.children)) {
        const percentile = this.childValue(record, 'percentile');
        const size = this.childValue(record, 'size');
        const percentChannel = this.childValue(record, 'percentchannel');
        if (percentile !== null && size !== null) {
          this.setValue(values, `percentile${this.numberToken(percentile)}_size`, size);
          this.setValue(values, `d${this.numberToken(percentile)}`, size);
        }
        if (percentile !== null && percentChannel !== null) {
          this.setValue(values, `percentile${this.numberToken(percentile)}`, percentChannel);
        }
      }
    }

    for (const section of this.elementsNamed(document, 'sizepercents')) {
      for (const record of Array.from(section.children)) {
        const size = this.childValue(record, 'size');
        const percentile = this.childValue(record, 'percentile');
        if (size !== null && percentile !== null) {
          this.setValue(values, `size${this.numberToken(size)}_percentile`, percentile);
        }
      }
    }

    const tabularRows = this.elementsNamed(document, 'tabularvalues')
      .flatMap(section => Array.from(section.children));
    for (let index = 0; index < tabularRows.length; index++) {
      const record = tabularRows[index];
      const percentChannel = this.childValue(record, 'percentchannel');
      const size = this.childValue(record, 'size');
      if (index < PsdTestReportComponent.ALL_PERCENTILES.length && percentChannel !== null) {
        this.setValue(values, `percentile${PsdTestReportComponent.ALL_PERCENTILES[index]}`, percentChannel);
      }
      const diameterIndex = index - PsdTestReportComponent.ALL_PERCENTILES.length;
      if (diameterIndex >= 0
        && diameterIndex < PsdTestReportComponent.DISPLAY_DIAMETERS.length
        && size !== null) {
        this.setValue(values, `diameter${PsdTestReportComponent.DISPLAY_DIAMETERS[diameterIndex]}`, size);
      }
    }

    for (const element of Array.from(document.getElementsByTagName('*'))) {
      if (element.children.length > 0) continue;
      const value = element.textContent?.trim();
      if (!value) continue;
      const key = element.localName.toLowerCase() === 'parameter'
        ? element.getAttribute('key')
        : element.localName;
      if (key) this.setValue(values, key, value);
    }

    return values;
  }

  private elementsNamed(document: Document, name: string): Element[] {
    return Array.from(document.getElementsByTagName('*'))
      .filter(element => element.localName.toLowerCase() === name.toLowerCase());
  }

  private childValue(element: Element, name: string): string | null {
    const child = Array.from(element.children)
      .find(candidate => candidate.localName.toLowerCase() === name.toLowerCase());
    const value = child?.textContent?.trim();
    return value || null;
  }

  private setValue(values: Map<string, string>, key: string, value: string): void {
    const normalizedKey = this.normalize(key);
    if (normalizedKey && !values.has(normalizedKey)) values.set(normalizedKey, value);
  }

  private numberToken(value: string): string {
    const parsed = this.toNumber(value);
    return parsed === undefined ? this.normalize(value) : String(parsed);
  }

  private normalize(value: string | null | undefined): string {
    return (value ?? '').replace(/[\s_\-/,().:%]+/g, '').toLowerCase();
  }

  private toNumber(value: string | null): number | undefined {
    if (!value) return undefined;
    const parsed = Number(value.replace(/,/g, ''));
    return Number.isFinite(parsed) ? parsed : undefined;
  }
}
